import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { AmountInput } from '@/components/amount-input';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { serverFieldErrors } from '@/components/settings-section';
import { toast } from '@/components/toast';
import { VatTabs } from '@/components/vat';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatTrn, useMe } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { useVatSettings, type VatSettings } from '@/lib/vat';

export const Route = createFileRoute('/taxes/vat/settings')({ component: VatSettingsPage });

const STAGGERS = [
  { value: 1, label: 'Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec' },
  { value: 2, label: 'Feb–Apr, May–Jul, Aug–Oct, Nov–Jan' },
  { value: 3, label: 'Mar–May, Jun–Aug, Sep–Nov, Dec–Feb' },
];

/** VAT centre › Settings (document 05, "VAT centre": third tab). */
function VatSettingsPage() {
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const settings = useVatSettings();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<VatSettings | null>(null);
  useEffect(() => {
    if (settings.data) setDraft(settings.data);
  }, [settings.data]);
  const save = useMutation({
    mutationFn: (d: VatSettings) =>
      api<VatSettings>(`/orgs/${orgId}/vat/settings`, {
        method: 'PATCH',
        body: JSON.stringify({
          frequency: d.frequency,
          quarterStartMonth: d.quarterStartMonth,
          largePurchaseThreshold: d.largePurchaseThreshold,
          disclosureThreshold: d.disclosureThreshold,
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vat', orgId] });
      toast('VAT settings saved.');
    },
  });
  const errors = serverFieldErrors(save.error);
  const s = settings.data;

  return (
    <>
      <PageHeader title="VAT" description="Filing frequency and the checks run before filing." />
      <VatTabs />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : settings.isPending || !draft ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : settings.isError ? (
        <EmptyState title="Could not load VAT settings" hint={settings.error.message} />
      ) : (
        <div className="grid max-w-3xl gap-4">
          <section className="rounded-lg border bg-card p-5">
            <h2 className="mb-3 text-base font-semibold">Registration</h2>
            <dl className="grid grid-cols-[200px_1fr] gap-y-2 text-sm">
              <dt className="text-muted-foreground">VAT registered</dt>
              <dd>{s?.vatRegistered ? 'Yes' : 'No'}</dd>
              <dt className="text-muted-foreground">TRN</dt>
              <dd className="tnum">{formatTrn(s?.trn ?? null) || '—'}</dd>
              <dt className="text-muted-foreground">Effective date</dt>
              <dd>{s?.registrationDate ? displayDate(s.registrationDate) : '—'}</dd>
              <dt className="text-muted-foreground">Emirate</dt>
              <dd>{s?.emirate ?? '—'}</dd>
            </dl>
            <p className="mt-3 text-xs text-muted-foreground">
              Registration details are kept in{' '}
              <Link to="/settings/company" className="text-primary hover:underline">
                Company settings
              </Link>
              . The emirate is the place of supply when a customer has none.
            </p>
          </section>
          <form
            className="grid gap-4 rounded-lg border bg-card p-5"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              save.mutate(draft);
            }}
          >
            <h2 className="text-base font-semibold">Tax periods and checks</h2>
            <ErrorBanner>
              {save.error && Object.keys(errors).length === 0 ? save.error.message : null}
            </ErrorBanner>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Filing frequency" hint="As assigned by the FTA.">
                {(id) => (
                  <NativeSelect
                    id={id}
                    disabled={!canEdit}
                    value={draft.frequency}
                    onChange={(e) =>
                      setDraft({ ...draft, frequency: e.target.value as VatSettings['frequency'] })
                    }
                  >
                    <option value="quarterly">Quarterly</option>
                    <option value="monthly">Monthly</option>
                  </NativeSelect>
                )}
              </Field>
              <Field label="Quarters" hint="The FTA staggers quarterly periods.">
                {(id) => (
                  <NativeSelect
                    id={id}
                    disabled={!canEdit || draft.frequency === 'monthly'}
                    value={draft.quarterStartMonth}
                    onChange={(e) =>
                      setDraft({ ...draft, quarterStartMonth: Number(e.target.value) })
                    }
                  >
                    {STAGGERS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field
                label={`Large purchase without TRN (${s?.currency ?? 'AED'})`}
                hint="Purchases above this claiming input VAT without the supplier's TRN are listed as exceptions."
                error={errors['largePurchaseThreshold']}
              >
                {(id) => (
                  <AmountInput
                    id={id}
                    className="h-9 rounded-md border border-input bg-card px-3 text-right text-sm tnum"
                    disabled={!canEdit}
                    value={draft.largePurchaseThreshold}
                    onValueChange={(v) => setDraft({ ...draft, largePurchaseThreshold: v || '0' })}
                  />
                )}
              </Field>
              <Field
                label={`Voluntary disclosure flag (${s?.currency ?? 'AED'})`}
                hint="A filed return whose figures later change by more than this is flagged."
                error={errors['disclosureThreshold']}
              >
                {(id) => (
                  <AmountInput
                    id={id}
                    className="h-9 rounded-md border border-input bg-card px-3 text-right text-sm tnum"
                    disabled={!canEdit}
                    value={draft.disclosureThreshold}
                    onValueChange={(v) => setDraft({ ...draft, disclosureThreshold: v || '0' })}
                  />
                )}
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">
              Returns already filed keep their periods when the frequency changes; new periods start
              the day after the last one. Currently flagged above {s?.currency}{' '}
              {formatAmount(s?.largePurchaseThreshold)} and {s?.currency}{' '}
              {formatAmount(s?.disclosureThreshold)}.
            </p>
            {canEdit ? (
              <div>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            ) : null}
          </form>
        </div>
      )}
    </>
  );
}
